import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ClipboardList, SearchIcon, PrinterIcon, PencilIcon, XIcon, CheckIcon } from 'lucide-react';
import { GatePassReceipt } from '../components/GatePassReceipt';
interface VehicleEntry {
  gatePassNo: string;
  vehicleNo: string;
  vehicleType: 'Truck' | 'Car' | 'Bike' | 'Van' | 'Trailer';
  user: string;
  dateTime: string;
}
const sampleData: VehicleEntry[] = [{
  gatePassNo: 'GP-2026-0001',
  vehicleNo: 'MH-12-AB-1234',
  vehicleType: 'Truck',
  user: 'Rajesh Kumar',
  dateTime: '2026-04-15 08:30 AM'
}, {
  gatePassNo: 'GP-2026-0002',
  vehicleNo: 'MH-14-CD-5678',
  vehicleType: 'Car',
  user: 'Priya Sharma',
  dateTime: '2026-04-15 09:15 AM'
}, {
  gatePassNo: 'GP-2026-0003',
  vehicleNo: 'GJ-05-EF-9012',
  vehicleType: 'Van',
  user: 'Amit Patel',
  dateTime: '2026-04-15 09:45 AM'
}, {
  gatePassNo: 'GP-2026-0004',
  vehicleNo: 'DL-01-GH-3456',
  vehicleType: 'Trailer',
  user: 'Sunil Verma',
  dateTime: '2026-04-15 10:00 AM'
}, {
  gatePassNo: 'GP-2026-0005',
  vehicleNo: 'RJ-14-IJ-7890',
  vehicleType: 'Truck',
  user: 'Deepak Singh',
  dateTime: '2026-04-15 10:30 AM'
}, {
  gatePassNo: 'GP-2026-0006',
  vehicleNo: 'KA-01-KL-2345',
  vehicleType: 'Car',
  user: 'Meena Reddy',
  dateTime: '2026-04-15 11:00 AM'
}, {
  gatePassNo: 'GP-2026-0007',
  vehicleNo: 'TN-09-MN-6789',
  vehicleType: 'Bike',
  user: 'Karthik Nair',
  dateTime: '2026-04-15 11:20 AM'
}, {
  gatePassNo: 'GP-2026-0008',
  vehicleNo: 'UP-32-OP-0123',
  vehicleType: 'Truck',
  user: 'Vikram Yadav',
  dateTime: '2026-04-15 11:45 AM'
}, {
  gatePassNo: 'GP-2026-0009',
  vehicleNo: 'MP-09-QR-4567',
  vehicleType: 'Van',
  user: 'Anita Gupta',
  dateTime: '2026-04-15 12:10 PM'
}, {
  gatePassNo: 'GP-2026-0010',
  vehicleNo: 'HR-26-ST-8901',
  vehicleType: 'Trailer',
  user: 'Rohit Malik',
  dateTime: '2026-04-15 12:30 PM'
}];
const vehicleTypeBadge: Record<string, {
  bg: string;
  text: string;
}> = {
  Truck: {
    bg: 'bg-amber-50 border-amber-200',
    text: 'text-amber-700'
  },
  Car: {
    bg: 'bg-blue-50 border-blue-200',
    text: 'text-blue-700'
  },
  Bike: {
    bg: 'bg-green-50 border-green-200',
    text: 'text-green-700'
  },
  Van: {
    bg: 'bg-purple-50 border-purple-200',
    text: 'text-purple-700'
  },
  Trailer: {
    bg: 'bg-rose-50 border-rose-200',
    text: 'text-rose-700'
  }
};
interface VehicleEntryDetailPageProps {
  userName: string;
}
export function VehicleEntryDetailPage({
  userName
}: VehicleEntryDetailPageProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [data, setData] = useState<VehicleEntry[]>(sampleData);
  const [reprintEntry, setReprintEntry] = useState<VehicleEntry | null>(null);
  const [editEntry, setEditEntry] = useState<VehicleEntry | null>(null);
  const [editForm, setEditForm] = useState<VehicleEntry | null>(null);
  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return data;
    const q = searchQuery.toLowerCase();
    return data.filter(entry => entry.gatePassNo.toLowerCase().includes(q) || entry.vehicleNo.toLowerCase().includes(q) || entry.vehicleType.toLowerCase().includes(q) || entry.user.toLowerCase().includes(q) || entry.dateTime.toLowerCase().includes(q));
  }, [searchQuery, data]);
  const handleEdit = (entry: VehicleEntry) => {
    setEditEntry(entry);
    setEditForm({
      ...entry
    });
  };
  const handleSaveEdit = () => {
    if (!editForm || !editEntry) return;
    setData(prev => prev.map(e => e.gatePassNo === editEntry.gatePassNo ? {
      ...editForm
    } : e));
    setEditEntry(null);
    setEditForm(null);
  };
  const handleCancelEdit = () => {
    setEditEntry(null);
    setEditForm(null);
  };
  return <div className="w-full max-w-6xl mx-auto" data-id="element-1544">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 p-5 xs:p-6 sm:p-8" data-id="element-1545">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6" data-id="element-1546">
          <div className="flex items-center gap-3" data-id="element-1547">
            <div className="bg-brand-teal/10 p-2 rounded-xl border border-brand-teal/20" data-id="element-1548">
              <ClipboardList className="w-6 h-6 text-brand-teal" data-id="element-1549" />
            </div>
            <div data-id="element-1550">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight" data-id="element-1551">
                Vehicle Entry Detail
              </h2>
              <p className="text-sm text-slate-500" data-id="element-1552">
                {filteredData.length} vehicle
                {filteredData.length !== 1 ? 's' : ''} in terminal
              </p>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative mb-5" data-id="element-1553">
          <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-400 pointer-events-none" data-id="element-1554" />
          <input type="text" placeholder="Search by gate pass, vehicle number, type, or user..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-teal/30 focus:border-brand-teal transition-all" data-id="element-1555" />
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-xl border border-slate-200" data-id="element-1556">
          <table className="w-full text-sm" data-id="element-1557">
            <thead data-id="element-1558">
              <tr className="bg-slate-50 border-b border-slate-200" data-id="element-1559">
                <th className="text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap" data-id="element-1560">
                  Gate Pass No.
                </th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap" data-id="element-1561">
                  Vehicle No.
                </th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap" data-id="element-1562">
                  Vehicle Type
                </th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap" data-id="element-1563">
                  User
                </th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap" data-id="element-1564">
                  Date & Time
                </th>
                <th className="text-center px-4 py-3 font-semibold text-slate-600 whitespace-nowrap" data-id="element-1565">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody data-id="element-1566">
              {filteredData.length === 0 ? <tr data-id="element-1567">
                  <td colSpan={6} className="text-center py-12 text-slate-400" data-id="element-1568">
                    No vehicles found matching your search.
                  </td>
                </tr> : filteredData.map((entry, index) => {
              const badge = vehicleTypeBadge[entry.vehicleType];
              return <motion.tr key={entry.gatePassNo} initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                delay: index * 0.03
              }} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60 transition-colors" data-id="element-1569">
                      <td className="px-4 py-3.5 font-mono text-xs font-medium text-brand-teal whitespace-nowrap" data-id="element-1570">
                        {entry.gatePassNo}
                      </td>
                      <td className="px-4 py-3.5 font-mono text-xs font-semibold text-slate-800 whitespace-nowrap" data-id="element-1571">
                        {entry.vehicleNo}
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap" data-id="element-1572">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium border ${badge.bg} ${badge.text}`} data-id="element-1573">
                          {entry.vehicleType}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-slate-700 whitespace-nowrap" data-id="element-1574">
                        {userName}
                      </td>
                      <td className="px-4 py-3.5 text-slate-500 whitespace-nowrap text-xs" data-id="element-1575">
                        {entry.dateTime}
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap" data-id="element-1576">
                        <div className="flex items-center justify-center gap-1.5" data-id="element-1577">
                          <button onClick={() => setReprintEntry(entry)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-brand-teal/10 text-brand-teal border border-brand-teal/20 hover:bg-brand-teal/20 transition-colors" title="Reprint Gate Pass" data-id="element-1578">
                            <PrinterIcon className="w-3.5 h-3.5" data-id="element-1579" />
                            Reprint
                          </button>
                          <button onClick={() => handleEdit(entry)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200 transition-colors" title="Edit Entry" data-id="element-1580">
                            <PencilIcon className="w-3.5 h-3.5" data-id="element-1581" />
                            Edit
                          </button>
                        </div>
                      </td>
                    </motion.tr>;
            })}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Reprint Modal */}
      {reprintEntry && <GatePassReceipt gatepassNumber={reprintEntry.gatePassNo} vehicleNumber={reprintEntry.vehicleNo} vehicleType={reprintEntry.vehicleType} entryTime={reprintEntry.dateTime} operatorName={userName} onClose={() => setReprintEntry(null)} data-id="element-1582" />}

      {/* Edit Modal */}
      <AnimatePresence data-id="element-1583">
        {editEntry && editForm && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={handleCancelEdit} data-id="element-1584">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} transition={{
          duration: 0.2
        }} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md p-6" data-id="element-1585">
              <div className="flex items-center justify-between mb-5" data-id="element-1586">
                <h3 className="text-lg font-bold text-slate-900" data-id="element-1587">
                  Edit Vehicle Entry
                </h3>
                <button onClick={handleCancelEdit} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-1588">
                  <XIcon className="w-5 h-5" data-id="element-1589" />
                </button>
              </div>

              <div className="space-y-4" data-id="element-1590">
                <div data-id="element-1591">
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5" data-id="element-1592">
                    Gate Pass No.
                  </label>
                  <input type="text" value={editForm.gatePassNo} disabled className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-400 cursor-not-allowed" data-id="element-1593" />
                </div>
                <div data-id="element-1594">
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5" data-id="element-1595">
                    Vehicle No.
                  </label>
                  <input type="text" value={editForm.vehicleNo} onChange={e => setEditForm({
                ...editForm,
                vehicleNo: e.target.value
              })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-teal/30 focus:border-brand-teal transition-all" data-id="element-1596" />
                </div>
                <div data-id="element-1597">
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5" data-id="element-1598">
                    Vehicle Type
                  </label>
                  <select value={editForm.vehicleType} onChange={e => setEditForm({
                ...editForm,
                vehicleType: e.target.value as VehicleEntry['vehicleType']
              })} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-teal/30 focus:border-brand-teal transition-all" data-id="element-1599">
                    <option value="Truck" data-id="element-1600">Truck</option>
                    <option value="Car" data-id="element-1601">Car</option>
                    <option value="Bike" data-id="element-1602">Bike</option>
                    <option value="Van" data-id="element-1603">Van</option>
                    <option value="Trailer" data-id="element-1604">Trailer</option>
                  </select>
                </div>
                <div data-id="element-1605">
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5" data-id="element-1606">
                    User
                  </label>
                  <input type="text" value={userName} disabled className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-400 cursor-not-allowed" data-id="element-1607" />
                </div>
              </div>

              <div className="flex items-center gap-3 mt-6" data-id="element-1608">
                <button onClick={handleSaveEdit} className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-teal text-white px-4 py-2.5 rounded-xl font-medium text-sm hover:bg-brand-tealHover transition-colors" data-id="element-1609">
                  <CheckIcon className="w-4 h-4" data-id="element-1610" />
                  Save Changes
                </button>
                <button onClick={handleCancelEdit} className="flex-1 inline-flex items-center justify-center gap-2 bg-white text-slate-600 px-4 py-2.5 rounded-xl font-medium text-sm border border-slate-200 hover:bg-slate-50 transition-colors" data-id="element-1611">
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}